import { useSearchParams } from "react-router-dom"
import { makeStyles } from "tss-react/mui"
import { DateTime } from "luxon"
import { DateRange, Range, RangeKeyDict } from "react-date-range"
import "react-date-range/dist/styles.css"
import "react-date-range/dist/theme/default.css"
import { Box, InputAdornment, Popover, TextField, useMediaQuery, useTheme } from "@mui/material"
import EventIcon from "@mui/icons-material/Event"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarDay } from "@fortawesome/pro-regular-svg-icons"
import React, { useMemo, useState } from "react"
import { DateFilterParams } from "./useInitialDateFilters"
import type { DatePickerRange } from "../forms/datePickerRangeTypes"
import { getDateRangeConstraints, intersectMaxDate, intersectMinDate } from "../forms/datePickerRangeTypes"
import { useDateTime } from "../../../modules/localization/useDateTime"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { useSelector } from "react-redux"
import { selectSelectedLocale } from "../../../modules/localization/localizationSelectors"
import { da, enUS, nb, sv } from "date-fns/locale"
import { LabeledField } from "../forms/LabeledField"

const useStyles = makeStyles()((theme) => ({
    dateFilter: {
        width: "15.5rem",
    },
    popoverPaper: {
        maxWidth: "100vw",
        overflowX: "auto",
    },
    popover: {
        padding: "1rem",
    },
    inputWrapper: {
        width: "100%",
    },
    input: {
        cursor: "pointer",
    },
    // "labeled" variant: rounded, slate-outlined field matching MeetingDatePicker (the existing
    // new-style date field); the label is rendered above by LabeledField.
    labeledInput: {
        "& .MuiOutlinedInput-root": {
            borderRadius: ".75rem",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.tailwind.slate[300],
        },
    },
    leadingIcon: {
        color: theme.palette.tailwind.slate[400],
    },
}))

export const dateLocaleMap: Record<string, Locale> = {
    "da-DK": da,
    "en-US": enUS,
    "nb-NO": nb,
    "sv-SE": sv,
}

export type DtDateRange = Readonly<{
    startDate: DateTime
    endDate: DateTime
}>

type DateRangeFilterProps = Readonly<{
    value: DtDateRange
    onChange: (newValue: DtDateRange) => void
    paramNameStart?: DateFilterParams
    paramNameEnd?: DateFilterParams
    label?: string
    /**
     * Which dates the filter may select, intersected with any explicit minDate/maxDate.
     * Defaults to "all": a filter only greys out days, it never blocks a submit, and the
     * selectable window here is a product decision per report rather than a safety net.
     */
    range?: DatePickerRange
    minDate?: DateTime
    maxDate?: DateTime
    inputClasses?: string
    dateFilterClasses?: string
    // "floating" (default) keeps the MUI floating label + trailing calendar icon.
    // "labeled" renders the label above the field (via LabeledField) with a leading calendar icon.
    variant?: "floating" | "labeled"
    size?: "small" | "medium"
    disabled?: boolean
}>

export const StandardDateRangeFilter = ({
    value,
    onChange,
    paramNameStart = DateFilterParams.START,
    paramNameEnd = DateFilterParams.END,
    label,
    range = "all",
    minDate,
    maxDate,
    inputClasses,
    dateFilterClasses,
    variant = "floating",
    size = "medium",
    disabled = false,
}: DateRangeFilterProps) => {
    const { classes, cx } = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const selectedLocale = useSelector(selectSelectedLocale)
    const [searchParams, setSearchParams] = useSearchParams()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const rangeConstraints = useMemo(() => getDateRangeConstraints(range), [range])
    const effectiveMinDate = intersectMinDate(rangeConstraints.minDate, minDate)
    const effectiveMaxDate = intersectMaxDate(rangeConstraints.maxDate, maxDate)
    const [state, setState] = useState<Range>({
        startDate: value.startDate.toJSDate() || new Date(),
        endDate: value.endDate.toJSDate() || new Date(),
        key: "selection",
    })

    const handleDateChange = (ranges: RangeKeyDict) => {
        setState(ranges["selection"])
    }

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        const newStartDate = state.startDate ? dateTimeFactory.fromISO(state?.startDate.toISOString()) : value.startDate

        const newEndDate = state?.endDate ? dateTimeFactory.fromISO(state?.endDate.toISOString()) : value.endDate

        if (!newStartDate.equals(value.startDate) || !newEndDate.equals(value.endDate)) {
            onChange({ startDate: newStartDate, endDate: newEndDate })
            searchParams.set(paramNameStart, newStartDate.toISODate() || "")
            searchParams.set(paramNameEnd, newEndDate.toISODate() || "")
            setSearchParams(searchParams)
        }
        setAnchorEl(null)
    }

    const open = Boolean(anchorEl)
    const isLabeled = variant === "labeled"
    const labelText = label ?? l10n.getString("standard-period-date-picker")
    const valueText = `${dateTimeFactory.formatDate(
        dateTimeFactory.fromISO(state.startDate!.toISOString()),
    )} - ${dateTimeFactory.formatDate(dateTimeFactory.fromISO(state.endDate!.toISOString()))}`

    const field = (
        <Box onClick={disabled ? undefined : handleClick} className={cx(classes.dateFilter, dateFilterClasses)}>
            <TextField
                size={size}
                disabled={disabled}
                InputProps={{
                    readOnly: true,
                    classes: { input: cx(classes.input, inputClasses) },
                    ...(isLabeled
                        ? {
                              startAdornment: (
                                  <InputAdornment position="start">
                                      <FontAwesomeIcon icon={faCalendarDay} className={classes.leadingIcon} />
                                  </InputAdornment>
                              ),
                          }
                        : {
                              endAdornment: (
                                  <InputAdornment sx={{ cursor: "pointer" }} position="end">
                                      <EventIcon />
                                  </InputAdornment>
                              ),
                          }),
                }}
                className={cx(classes.inputWrapper, isLabeled && classes.labeledInput)}
                value={valueText}
                label={isLabeled ? undefined : labelText}
            />
        </Box>
    )

    return (
        <Box>
            {isLabeled ? (
                <LabeledField label={labelText} bold={false} gap=".25rem">
                    {field}
                </LabeledField>
            ) : (
                field
            )}

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                classes={{ paper: classes.popoverPaper }}
            >
                <Box className={classes.popover}>
                    <DateRange
                        onChange={handleDateChange}
                        minDate={effectiveMinDate?.toJSDate()}
                        maxDate={effectiveMaxDate?.toJSDate()}
                        moveRangeOnFirstSelection={false}
                        ranges={[state]}
                        months={isMobile ? 1 : 2}
                        direction={isMobile ? "vertical" : "horizontal"}
                        locale={dateLocaleMap[selectedLocale] ?? da}
                    />
                </Box>
            </Popover>
        </Box>
    )
}
